import React, { useId } from 'react';
import { Box, Card, Divider, SxProps, Theme, Typography } from '@mui/material';

interface SectionProps {
  title?: React.ReactNode;
  /** One line under the title saying what the section is for. */
  description?: React.ReactNode;
  /** Right-aligned controls for the section (a text button, a count, a menu). */
  action?: React.ReactNode;
  children: React.ReactNode;
  /** For lists and tables that run edge to edge inside the panel. */
  disablePadding?: boolean;
  id?: string;
  sx?: SxProps<Theme>;
}

/**
 * A titled panel: the theme's flat bordered card with a header row and a body.
 * Use divider rows or list items inside it - never another card.
 */
const Section: React.FC<SectionProps> = ({ title, description, action, children, disablePadding = false, id, sx }) => {
  const generatedId = useId();
  const headingId = `${id || generatedId}-title`;
  const hasHeader = Boolean(title || description || action);
  return (
    <Card component="section" id={id} aria-labelledby={title ? headingId : undefined} sx={sx}>
      {hasHeader && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.5,
              px: 2.5,
              py: 2,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {title && (
                <Typography id={headingId} variant="h6" component="h2" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1.4 }}>
                  {title}
                </Typography>
              )}
              {description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {description}
                </Typography>
              )}
            </Box>
            {action && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>{action}</Box>}
          </Box>
          <Divider />
        </>
      )}
      <Box sx={{ p: disablePadding ? 0 : 2.5 }}>{children}</Box>
    </Card>
  );
};

export default Section;
